import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import '../Admin/AdminPanel.css';
import { motion } from 'framer-motion';
import ProductTemplate from '../Products/ProductTemplate';
import ProductEditorCanvas from './ProductEditorCanvas';
import { addToCart } from '../../services/cartService';
import { useAuth } from '../../contexts/AuthContext';
import { FaCamera, FaEdit, FaTrash, FaEllipsisV } from 'react-icons/fa';

const PRODUCT_TYPES = [
  { value: 'coffee', label: 'Coffee' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'merch', label: 'Merch' },
];

const DEFAULT_MARGINS = { top: 0, bottom: 0, left: 0, right: 0 };
const DEFAULT_TABS = {
  producer: [],
  aboutBeans: [],
  recipes: [],
};
const DEFAULT_DETAILS_TAB = { details: [] };

const DEFAULT_PRODUCT = {
  name: '',
  price: '',
  image: '',
  imageMargin: { top: 0, bottom: 0, left: 0, right: 0 },
  type: 'coffee',
  description: '',
  shopCategory: 'coffee',
  tags: [],
  origin: '',
  producer: '',
  altitude: '',
  process: '',
  varietal: '',
  notes: '',
  roastOptions: [{ label: '', price: '' }],
  quantityAvailable: '',
  tabs: {
    producer: [],
    aboutBeans: [],
    recipes: [],
  },
  backgroundImage: '',
  beanTypes: ['filter', 'espresso'],
};

const ProductManagement = () => {
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    image: '',
    imageMargin: { ...DEFAULT_MARGINS },
    type: 'coffee',
    description: '',
    shopCategory: 'coffee',
    tags: [],
    origin: '',
    producer: '',
    altitude: '',
    process: '',
    varietal: '',
    notes: '',
    roastOptions: [{ label: '', price: '' }],
    quantityAvailable: '',
    tabs: { ...DEFAULT_TABS },
    backgroundImage: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [layoutConfig, setLayoutConfig] = useState({
    image: { src: '', x: 0, y: 0, width: 0, height: 0 },
    textBlocks: {
      title: { value: '', x: 700, y: 100, fontSize: 32, fontWeight: 'bold', color: '#111' },
      origin: { value: '', x: 0, y: 0, fontSize: 16, fontWeight: 'normal', color: '#222' },
      producer: { value: '', x: 60, y: 100, fontSize: 16, fontWeight: 'normal', color: '#222' },
      altitude: { value: '', x: 60, y: 140, fontSize: 16, fontWeight: 'normal', color: '#222' },
      process: { value: '', x: 60, y: 180, fontSize: 16, fontWeight: 'normal', color: '#222' },
      varietal: { value: '', x: 60, y: 220, fontSize: 16, fontWeight: 'normal', color: '#222' },
      notes: { value: '', x: 60, y: 260, fontSize: 16, fontWeight: 'normal', color: '#222' },
    }
  });
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [lastSavedLayout, setLastSavedLayout] = useState(null);
  const [mode, setMode] = useState('edit'); // 'edit' or 'add'
  const [addLoading, setAddLoading] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('producer');
  const { currentUser } = useAuth();
  const [cartMessage, setCartMessage] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [filter, setFilter] = useState({ category: '', keyword: '', status: '' });

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      const snap = await getDocs(collection(db, 'products'));
      const list = [];
      snap.forEach(docSnap => list.push({ id: docSnap.id, ...docSnap.data() }));
      setProducts(list);
      if (list.length && !selectedProductId) setSelectedProductId(list[0].id);
    };
    fetchProducts();
  }, []);

  // Load selected product into editProduct
  useEffect(() => {
    if (mode === 'add') {
      setEditProduct({ ...DEFAULT_PRODUCT, tabs: { ...DEFAULT_TABS } });
      setActiveTab('producer');
    } else if (selectedProductId) {
      const prod = products.find(p => p.id === selectedProductId);
      if (prod) {
        // Ensure each tab is always an array
        const cleanTabs = {
          producer:   Array.isArray(prod.tabs?.producer)   ? prod.tabs.producer   : [],
          aboutBeans: Array.isArray(prod.tabs?.aboutBeans) ? prod.tabs.aboutBeans : [],
          recipes:    Array.isArray(prod.tabs?.recipes)    ? prod.tabs.recipes    : [],
        };
        setEditProduct({ ...prod, tabs: cleanTabs });
        setActiveTab('producer');
      }
    }
  }, [selectedProductId, mode, products]);

  // Load selected product's layoutConfig and fields
  useEffect(() => {
    if (!selectedProductId) return;
    const prod = products.find(p => p.id === selectedProductId);
    if (prod) {
      const loadedLayout = prod.layoutConfig || {
        image: { src: prod.image || '', x: 100, y: 100, width: 300, height: 320 },
        textBlocks: {
          title: { value: prod.name || '', x: 700, y: 100, fontSize: 32, fontWeight: 'bold', color: '#111' },
          origin: { value: prod.origin || '', x: 60, y: 60, fontSize: 16, fontWeight: 'normal', color: '#222' },
          producer: { value: prod.producer || '', x: 60, y: 100, fontSize: 16, fontWeight: 'normal', color: '#222' },
          altitude: { value: prod.altitude || '', x: 60, y: 140, fontSize: 16, fontWeight: 'normal', color: '#222' },
          process: { value: prod.process || '', x: 60, y: 180, fontSize: 16, fontWeight: 'normal', color: '#222' },
          varietal: { value: prod.varietal || '', x: 60, y: 220, fontSize: 16, fontWeight: 'normal', color: '#222' },
          notes: { value: prod.notes || '', x: 60, y: 260, fontSize: 16, fontWeight: 'normal', color: '#222' },
        }
      };
      setLayoutConfig(loadedLayout);
      setLastSavedLayout(loadedLayout);
    }
  }, [selectedProductId, products]);

  // Handle roast options
  const handleRoastOptionChange = (idx, field, value) => {
    setNewProduct((prev) => ({
      ...prev,
      roastOptions: prev.roastOptions.map((w, i) => (i === idx ? { ...w, [field]: value } : w)),
    }));
  };
  const handleAddRoastOption = () =>
    setNewProduct((prev) => ({ ...prev, roastOptions: [...prev.roastOptions, { label: '', price: '' }] }));
  const handleRemoveRoastOption = (idx) =>
    setNewProduct((prev) => ({ ...prev, roastOptions: prev.roastOptions.filter((_, i) => i !== idx) }));

  // Handle tag checkboxes
  const handleTagChange = (tag) => {
    setNewProduct((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  // Handle image margin
  const handleMarginChange = (side, value) => {
    setNewProduct((prev) => ({
      ...prev,
      imageMargin: { ...prev.imageMargin, [side]: Number(value) },
    }));
  };

  // Handle tab content
  const handleTabChange = (tab, field, value) => {
    setNewProduct((prev) => ({
      ...prev,
      tabs: {
        ...prev.tabs,
        [tab]: typeof prev.tabs[tab] === 'object'
          ? { ...prev.tabs[tab], [field]: value }
          : value,
      },
    }));
  };

  // Handle type/category change
  const handleTypeChange = (e) => {
    const type = e.target.value;
    setNewProduct((prev) => ({
      ...prev,
      type,
      shopCategory: type,
      // Reset fields not relevant to new type
      ...(type === 'coffee'
        ? { roastOptions: [{ label: '', price: '' }], tabs: { ...DEFAULT_TABS } }
        : {}),
    }));
  };

  const handleAddNew = async () => {
    if (unsaved && !window.confirm('You have unsaved changes. Discard and add a new product?')) return;
    // Prompt for product type
    let type = window.prompt('Enter product type: coffee, equipment, or merch', 'coffee');
    if (!type) return;
    type = type.toLowerCase();
    if (!['coffee', 'equipment', 'merch'].includes(type)) {
      alert('Invalid type. Please enter coffee, equipment, or merch.');
      return;
    }
    setAddLoading(true);
    try {
      // Set up default tabs based on type
      let tabs = type === 'coffee' ? { ...DEFAULT_TABS } : { ...DEFAULT_DETAILS_TAB };
      let shopCategory = type;
      // Create a new product in Firestore with default values
      const productToAdd = {
        ...DEFAULT_PRODUCT,
        type,
        shopCategory,
        tabs,
        desktopTabs: tabs,
        mobileTabs: tabs,
      };
      if (type !== 'coffee') {
        delete productToAdd.beanTypes;
      }
      const docRef = await addDoc(collection(db, 'products'), productToAdd);
      const snap = await getDocs(collection(db, 'products'));
      const list = [];
      snap.forEach(docSnap => list.push({ id: docSnap.id, ...docSnap.data() }));
      setProducts(list);
      setMode('edit');
      setSelectedProductId(docRef.id);
      setUnsaved(false);
    } finally {
      setAddLoading(false);
    }
  };
  const handleSelectProduct = (id) => {
    setMode('edit');
    setSelectedProductId(id);
  };

  // Defensive sanitize before saving
  function sanitizeTabs(tabs) {
    const newTabs = {};
    for (const tabKey of Object.keys(tabs)) {
      const blocks = Array.isArray(tabs[tabKey]) ? tabs[tabKey] : [];
      newTabs[tabKey] = blocks
        .filter(Boolean)
        .map(block => {
          if (
            block &&
            block.type === 'button' &&
            (block.buttonType === 'weightSelector' || block.buttonType === 'beanTypeSelector')
          ) {
            return {
              ...block,
              options: Array.isArray(block.options) ? block.options : [],
              label: typeof block.label === 'string' ? block.label : '',
              unit: typeof block.unit === 'string' ? block.unit : '',
            };
          }
          return block;
        });
    }
    return newTabs;
  }

  const handleSave = async (data) => {
    // Force each tab to be an array of valid blocks
    const cleanTabs = ['producer','aboutBeans','recipes'].reduce((acc, key) => {
      const arr = Array.isArray(data.tabs?.[key]) ? data.tabs[key] : [];
      acc[key] = arr.filter(b => b && typeof b.type === 'string');
      return acc;
    }, {});
    data.tabs = cleanTabs;
    let dataToSave = { ...data };
    if (Array.isArray(editProduct.beanTypes) && editProduct.beanTypes.length > 0) {
      dataToSave.beanTypes = editProduct.beanTypes;
    } else {
      delete dataToSave.beanTypes;
    }
    // Sanitize tabs before saving
    if (dataToSave.tabs) {
      dataToSave.tabs = sanitizeTabs(dataToSave.tabs);
    }
    if (mode === 'add') {
      if (!window.confirm('Are you sure you want to add this product?')) return;
      setAddLoading(true);
      try {
        await addDoc(collection(db, 'products'), dataToSave);
        // Refresh products
        const snap = await getDocs(collection(db, 'products'));
        const list = [];
        snap.forEach(docSnap => list.push({ id: docSnap.id, ...docSnap.data() }));
        setProducts(list);
        setMode('edit');
        setSelectedProductId(list[list.length - 1].id);
        setAddSuccess(true);
        setTimeout(() => setAddSuccess(false), 2000);
        setUnsaved(false);
      } finally {
        setAddLoading(false);
      }
    } else if (mode === 'edit' && selectedProductId) {
      await updateDoc(doc(db, 'products', selectedProductId), dataToSave);
      setProducts(products.map(p => p.id === selectedProductId ? { ...p, ...dataToSave } : p));
      setUnsaved(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    await deleteDoc(doc(db, 'products', id));
    setProducts(products.filter((p) => p.id !== id));
    if (selectedProductId === id) setSelectedProductId('');
  };

  // Add to cart handler
  const handleAddToCart = async (product) => {
    if (!currentUser) {
      setCartMessage('Please log in to add to cart.');
      setTimeout(() => setCartMessage(null), 2000);
      return;
    }
    const result = await addToCart(product, currentUser.uid);
    if (result.success) {
      setCartMessage('Added to cart!');
    } else {
      setCartMessage(result.error || 'Failed to add to cart.');
    }
    setTimeout(() => setCartMessage(null), 2000);
  };

  // --- Add handler for Best Seller toggle ---
  const handleBestSellerToggle = async (productId, currentValue) => {
    try {
      await updateDoc(doc(db, 'products', productId), { bestSeller: !currentValue });
      setProducts(products => products.map(p => p.id === productId ? { ...p, bestSeller: !currentValue } : p));
    } catch (err) {
      alert('Failed to update Best Seller status.');
    }
  };

  const VisualEditor = ({ productId, product, onSave }) => {
    const [activeTab, setActiveTab] = useState('producer');
    const [editState, setEditState] = useState(product);
    const handleFieldChange = (field, value) => {
      setEditState(prev => ({ ...prev, [field]: value }));
    };
    const handleTabFieldChange = (tab, field, value) => {
      setEditState(prev => ({
        ...prev,
        tabs: {
          ...prev.tabs,
          [tab]: typeof prev.tabs[tab] === 'object'
            ? { ...prev.tabs[tab], [field]: value }
            : value,
        },
      }));
    };
    const handleImageChange = () => {
      const url = prompt('Enter image URL:');
      if (url) handleFieldChange('image', url);
    };
    const handleVideoChange = () => {
      const url = prompt('Enter video URL:');
      if (url) handleTabFieldChange('producer', 'videoUrl', url);
    };
    const handleRecipeImageChange = () => {
      const url = prompt('Enter recipe image URL:');
      if (url) handleTabFieldChange('recipe', 'imageUrl', url);
    };
    const handleBackgroundImageChange = () => {
      const url = prompt('Enter background image URL:');
      if (url) handleFieldChange('backgroundImage', url);
    };
    const save = () => onSave(editState);
    // Layout matches live product page
  return (
      <div style={{
        width: 1300,
        height: 800,
        margin: '40px auto',
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid #000',
        boxShadow: '0 8px 32px 0 rgba(195, 195, 195, 0.71), 0 1.5px 0 #fff inset',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        borderRadius: 0,
        overflow: 'visible',
        minHeight: 800,
      }}>
        {/* Tabs */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', display: 'flex', borderBottom: '1px solid #ccc', background: 'rgba(255,255,255,0.85)' }}>
          {['producer', 'aboutBeans', 'recipes'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                fontWeight: 600,
                fontSize: 13,
                minWidth: 120,
                height: 40,
                background: activeTab === tab ? '#000' : '#f4f4f4',
                color: activeTab === tab ? '#fff' : '#444',
                border: '1px solid #ccc',
                borderBottom: activeTab === tab ? 'none' : '1px solid #ccc',
                borderRadius: '8px 8px 0 0',
                textTransform: 'uppercase',
                letterSpacing: 0.7,
                cursor: 'pointer',
                fontFamily: 'Inter, Arial, sans-serif',
                padding: '0 16px',
                boxShadow: activeTab === tab ? '0 -2px 4px rgba(0,0,0,0.05)' : 'none',
                zIndex: activeTab === tab ? 2 : 1,
                position: 'relative',
                marginLeft: 0,
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
        {/* Main Content */}
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', position: 'relative', padding: 64 }}>
          {/* Left: Details */}
          <div style={{ flex: 1, maxWidth: 520, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            {activeTab === 'producer' && (
              <>
                <div style={{ fontWeight: 700, fontSize: 38, marginBottom: 32, textAlign: 'left' }}>
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    style={{ outline: '1px dashed #aaa' }}
                    onBlur={e => handleFieldChange('producer', e.target.innerText)}
                  >{editState.producer}</span>
                </div>
                <div style={{ textAlign: 'left', fontSize: 17 }}>
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    style={{ outline: '1px dashed #aaa' }}
                    onBlur={e => handleTabFieldChange('producer', 'description', e.target.innerText)}
                  >{editState.tabs.producer.description}</span>
                </div>
                <button onClick={handleVideoChange} style={{ marginTop: 16 }}>Change Video</button>
                {editState.tabs.producer.videoUrl && (
                  <video
                    src={editState.tabs.producer.videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ width: editState.tabs.producer.videoWidth, height: editState.tabs.producer.videoHeight, marginTop: 24 }}
                  />
                )}
              </>
            )}
            {activeTab === 'recipes' && (
              <>
                <div style={{ fontWeight: 700, fontSize: 38, marginBottom: 32, textAlign: 'left' }}>
                  TRY THIS RECIPE
                </div>
                <div style={{ marginBottom: 24 }}>
                  <span role="img" aria-label="bean">☕</span> Dose: <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'dose', e.target.innerText)}>{editState.tabs.recipe.dose}</span><br />
                  <span role="img" aria-label="water">💧</span> Water: <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'water', e.target.innerText)}>{editState.tabs.recipe.water}</span><br />
                  <span role="img" aria-label="grinder">🛠️</span> Grind size: <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'grindSize', e.target.innerText)}>{editState.tabs.recipe.grindSize}</span><br />
                  <span role="img" aria-label="timer">⏲️</span> Brew time: <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'brewTime', e.target.innerText)}>{editState.tabs.recipe.brewTime}</span><br />
                  <span role="img" aria-label="thermometer">🌡️</span> <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'temperature', e.target.innerText)}>{editState.tabs.recipe.temperature}</span><br />
                  <hr style={{ margin: '18px 0' }} />
                  Target TDS: <span contentEditable suppressContentEditableWarning style={{ outline: '1px dashed #aaa' }} onBlur={e => handleTabFieldChange('recipe', 'tds', e.target.innerText)}>{editState.tabs.recipe.tds}</span>
                </div>
                <div style={{ fontSize: 16, color: '#222' }}>
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    style={{ outline: '1px dashed #aaa' }}
                    onBlur={e => handleTabFieldChange('recipe', 'instructions', e.target.innerText)}
                  >{editState.tabs.recipe.instructions}</span>
                </div>
                <button onClick={handleRecipeImageChange} style={{ marginTop: 16 }}>Change Recipe Image</button>
                {editState.tabs.recipe.imageUrl && (
                  <img
                    src={editState.tabs.recipe.imageUrl}
                    alt="Recipe"
                    style={{ width: editState.tabs.recipe.imageWidth, height: editState.tabs.recipe.imageHeight, marginTop: 24 }}
                  />
                )}
              </>
            )}
          </div>
          {/* Center: Main Image */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <img
              src={editState.image}
              alt={editState.name}
              style={{ width: 400, height: 420, objectFit: 'contain', cursor: 'pointer', border: '2px dashed #aaa', background: '#fff' }}
              onClick={handleImageChange}
            />
          </div>
          {/* Right: Name, Price, Buy */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
            <div
              contentEditable
              suppressContentEditableWarning
            style={{
                fontWeight: 'bold',
                fontSize: 32,
                color: '#111',
                outline: '1px dashed #aaa',
                background: 'rgba(255,255,255,0.8)',
                padding: '4px 12px',
                borderRadius: 6,
                minWidth: 120,
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onBlur={e => setEditState(prev => ({
                ...prev,
                name: e.target.innerText,
                layoutConfig: {
                  ...prev.layoutConfig,
                  textBlocks: {
                    ...prev.layoutConfig?.textBlocks,
                    title: {
                      ...prev.layoutConfig?.textBlocks?.title,
                      value: e.target.innerText,
                    },
                  },
                },
              }))}
            >
              {editState.name || 'Product Title'}
            </div>
            <div
              contentEditable
              suppressContentEditableWarning
              style={{ fontSize: 14, marginTop: 8, marginBottom: 18, color: '#444', fontWeight: 400, marginLeft: -50, outline: '1px dashed #aaa' }}
              onBlur={e => handleFieldChange('price', e.target.innerText)}
            >{editState.price} Qr</div>
            <button style={{ background: '#d0d0d0', color: '#111', border: 'none', fontWeight: 700, fontSize: 14, padding: 0, marginTop: 20, marginLeft: 25, cursor: 'pointer', width: 120, height: 45, textTransform: 'uppercase', letterSpacing: 1, boxShadow: 'none', outline: 'none' }}>BUY</button>
          </div>
        </div>
        <button onClick={save} style={{ position: 'absolute', right: 16, bottom: 16, zIndex: 10 }}>Save</button>
        <button onClick={() => {
          const url = prompt('Enter background image URL:');
          if (url) handleFieldChange('backgroundImage', url);
        }} style={{ marginTop: 12 }}>Set Background Image</button>
      </div>
    );
  };

  const handleReset = () => {
    if (lastSavedLayout) setLayoutConfig(lastSavedLayout);
  };

  // Add handler for beanTypes
  const handleBeanTypeChange = (type) => {
    setEditProduct(prev => {
      const arr = Array.isArray(prev.beanTypes) ? prev.beanTypes : [];
      return {
        ...prev,
        beanTypes: arr.includes(type)
          ? arr.filter(t => t !== type)
          : [...arr, type],
      };
    });
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', flexDirection: 'row', gap: 32 }}>
        <div style={{ minWidth: 320, maxWidth: 340 }}>
          <button onClick={handleAddNew} style={{ marginBottom: 16, width: '100%' }}>Add New Product</button>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Products</div>
          <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            {products.map((p) => (
              <div
                key={p.id}
                onClick={() => { setSelectedProductId(p.id); setMode('edit'); }}
                style={{
                  padding: 12,
                  cursor: 'pointer',
                  background: selectedProductId === p.id && mode === 'edit' ? '#f4f4f4' : '#fff',
                  borderBottom: '1px solid #eee',
                  fontWeight: 500,
                  fontSize: 15,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>
                  {p.name || '(Untitled)'}
                  <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>
                    [{p.id?.slice(0, 6) || 'no-id'}]
                  </span>
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!p.bestSeller}
                    onChange={e => { e.stopPropagation(); handleBestSellerToggle(p.id, !!p.bestSeller); }}
                    style={{ marginRight: 4 }}
                  />
                  Best Seller
                </label>
                <button
                  onClick={e => { e.stopPropagation(); handleDeleteProduct(p.id); }}
                  style={{ color: '#fff', background: '#e00', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 13, cursor: 'pointer' }}
                  title="Delete Product"
                >
                  Delete
                </button>
                <button
                  onClick={e => { e.stopPropagation(); handleAddToCart(p); }}
                  style={{ color: '#fff', background: '#007bff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 13, cursor: 'pointer', marginLeft: 8 }}
                  title="Add to Cart"
                >
                  Add to Cart
                </button>
            </div>
          ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 900 }}>
          {editProduct && (
            <>
              {editProduct.type === 'coffee' ? (
              <div style={{ display: 'flex', gap: 0, marginBottom: 12 }}>
                {['producer', 'aboutBeans', 'recipes'].map(tab => (
          <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
            style={{
                      fontWeight: 600,
                      fontSize: 13,
                      minWidth: 120,
                      height: 40,
                      background: activeTab === tab ? '#000' : '#f4f4f4',
                      color: activeTab === tab ? '#fff' : '#444',
                      border: '1px solid #ccc',
                      borderBottom: activeTab === tab ? 'none' : '1px solid #ccc',
                      borderRadius: '8px 8px 0 0',
                      textTransform: 'uppercase',
                      letterSpacing: 0.7,
              cursor: 'pointer',
                      fontFamily: 'Inter, Arial, sans-serif',
                      padding: '0 16px',
                      boxShadow: activeTab === tab ? '0 -2px 4px rgba(0,0,0,0.05)' : 'none',
                      zIndex: activeTab === tab ? 2 : 1,
                      position: 'relative',
                      marginLeft: 0,
                      transition: 'background 0.2s, color 0.2s',
                    }}
                  >
                    {tab === 'aboutBeans' ? 'ABOUT BEANS' : tab.toUpperCase()}
          </button>
                ))}
              </div>
              ) : (
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Details</div>
              )}
              <ProductEditorCanvas
                productId={selectedProductId}
                tabs={{
                  desktop: editProduct.desktopTabs || editProduct.tabs || {},
                  mobile: editProduct.mobileTabs || {},
                }}
                activeTab={editProduct.type === 'coffee' ? activeTab : 'details'}
                onTabsChange={tabs => {
                  setUnsaved(true);
                  setEditProduct(prev => ({
                    ...prev,
                    desktopTabs: tabs.desktop,
                    mobileTabs: tabs.mobile,
                    tabs: tabs.desktop, // for backward compatibility
                  }));
                }}
                onSave={tabs => handleSave({ ...editProduct, tabs })}
                type={editProduct.type}
                shopCategory={editProduct.shopCategory}
              />
            </>
          )}
        </div>
      </div>
      {addSuccess && (
        <div style={{ position: 'fixed', top: 80, right: 40, background: '#0a0', color: '#fff', padding: '12px 24px', borderRadius: 8, fontWeight: 700, fontSize: 18, zIndex: 2000 }}>
          Product added successfully!
        </div>
      )}
      {cartMessage && (
        <div style={{ position: 'fixed', top: 120, right: 40, background: '#007bff', color: '#fff', padding: '12px 24px', borderRadius: 8, fontWeight: 700, fontSize: 18, zIndex: 2000 }}>
          {cartMessage}
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
